import { Navigate, useLocation } from "react-router-dom";
import { ReactNode } from "react";
import { useAuth, AppRole } from "./AuthProvider";
import { Loader2 } from "lucide-react";
import { useEnvStore } from "@/os/env/useEnvStore";
import { isSandboxAllowedHere } from "./sandboxGuard";

export function RequireRole({
  roles,
  children,
}: {
  roles?: AppRole[];
  children: ReactNode;
}) {
  const { user, roles: userRoles, loading } = useAuth();
  const env = useEnvStore((s) => s.env);
  const loc = useLocation();

  // No env yet → let /os decide (login first, then env picker / auto-prod).
  if (!env) return <Navigate to="/os" replace />;

  // Sandbox bypasses auth, but only on hostnames where the demo is allowed.
  // On the published production domain we ignore a stored `sandbox` value
  // (e.g. a malicious localStorage edit) and fall through to real auth.
  if (env === "sandbox" && isSandboxAllowedHere()) return <>{children}</>;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/os/login" state={{ from: loc.pathname }} replace />;
  }

  if (roles && roles.length > 0 && !userRoles.some((r) => roles.includes(r))) {
    return <Navigate to="/os" replace />;
  }

  return <>{children}</>;
}